/**
 * Editor de artes.
 *
 * O desenho é fixo e o conteúdo é variável: quem opera não escolhe fonte, cor
 * nem margem — escolhe um dos quatro modelos, escreve, aponta uma foto e
 * ajusta uma única variável de layout. Não existe estado em que saia uma arte
 * fora do sistema visual, e é isso que o editor compra.
 *
 * O que ele NÃO faz, de propósito: publicar. A arte gerada entra na
 * biblioteca de mídia como qualquer upload, e o agendamento que já existe
 * segue sem saber que aquilo veio daqui. Arte personalizada continua entrando
 * por "Mídia", como sempre entrou.
 *
 * ---------------------------------------------------------------------------
 * POR QUE A FOTO VEM DE /media/:id/arquivo E NÃO DA public_url
 * ---------------------------------------------------------------------------
 * Desenhar no canvas uma imagem vinda de outra origem CONTAMINA o canvas, e a
 * partir daí `toBlob()` lança SecurityError. O CloudFront não manda cabeçalho
 * de CORS, então a foto é buscada pela nossa própria origem. Trocar por
 * `publicUrl` aqui faz o preview continuar perfeito e só a exportação falhar.
 */
import {
  ALTURA,
  LARGURA,
  SEGURO_BASE,
  arteVazia,
  clampDy,
  estouro,
  plano,
  type Arte,
  type Template
} from "../../arte/layout.js";
import {
  Api,
  type LogoGuardado,
  type Marca,
  type MidiaGuardada
} from "../api.js";
import { carregarFontes, desenhar, type Fotos } from "../arte/desenho.js";
import { el, limpar } from "../dom.js";
import { marcaAtual } from "../marca.js";
import { avisar, type Tela } from "../tela.js";
import { editorCarrosselNexlab } from "./artes-nexlab.js";

const RASCUNHO = "nex-social:arte-rascunho";

// ------------------------------------------------------------------- tela

/**
 * A tela Artes: o editor do SISTEMA VISUAL da marca escolhida no topo.
 *
 * Escolhe pelo `sistemaVisual` (tabela `brands`), nunca pela marca — o
 * CLAUDE.md proíbe marca em condicional. Um cliente que use o sistema da Nex
 * Lab é uma linha no banco, não um `if` aqui.
 *
 * Marca sem sistema não ganha os modelos de outra: uma arte do cliente com a
 * cara do AntiFila é o tipo de erro que só aparece publicado.
 */
export function telaArtes(): Tela {
  return {
    titulo: "Artes",
    async montar(raiz) {
      const marca = marcaAtual();
      switch (marca.sistemaVisual) {
        case "antifila":
          return editorQuatroModelos(raiz, marca);
        case "nexlab":
          return editorCarrosselNexlab(raiz, marca);
        default:
          raiz.appendChild(
            el(
              "div",
              { class: "vazio" },
              el("p", {}, `${marca.nome} ainda não tem modelos de arte.`),
              el(
                "p",
                { class: "sub" },
                "A arte entra pronta pela aba Mídia. Os modelos de outra marca " +
                  "não são oferecidos aqui de propósito."
              )
            )
          );
      }
    }
  };
}

const MODELOS: { id: Template; nome: string; resumo: string }[] = [
  {
    id: "foto",
    nome: "Foto",
    resumo: "Foto sangrada com faixa de texto embaixo"
  },
  {
    id: "manif",
    nome: "Manifesto",
    resumo: "Só tipografia, com uma palavra em destaque"
  },
  {
    id: "comp",
    nome: "Antes / depois",
    resumo: "Duas fotos lado a lado e duas listas"
  },
  { id: "passo", nome: "Passo", resumo: "Número grande e barra de progresso" }
];

/** Quais slots de foto cada modelo usa. */
const SLOTS: Record<Template, { slot: "A" | "B"; rotulo: string }[]> = {
  foto: [{ slot: "A", rotulo: "Foto" }],
  manif: [],
  comp: [
    { slot: "A", rotulo: "Antes" },
    { slot: "B", rotulo: "Depois" }
  ],
  passo: []
};

// --------------------------------------------------------------- rascunho

/**
 * Guarda o rascunho no navegador de quem está editando.
 *
 * Não vai para o servidor: é conveniência de sessão, não dado do sistema. A
 * arte só vira algo que o sistema conhece quando é gerada e entra na
 * biblioteca.
 *
 * Toda leitura e toda escrita em try/catch — em aba anônima ou com dados de
 * site bloqueados, o acesso LANÇA em vez de devolver vazio, e um editor que
 * não abre por causa disso seria uma troca péssima.
 */
function lerRascunho(brand: string): Arte {
  try {
    // A chave sem marca é a de antes da migration 007, quando só o AntiFila
    // tinha editor. Lida como reserva para o rascunho em andamento não sumir
    // no deploy.
    const cru =
      localStorage.getItem(`${RASCUNHO}:${brand}`) ??
      localStorage.getItem(RASCUNHO);
    if (!cru) return arteVazia();
    // Mesclado sobre o padrão: campo novo no futuro não quebra rascunho velho.
    return { ...arteVazia(), ...(JSON.parse(cru) as Partial<Arte>) };
  } catch {
    return arteVazia();
  }
}

function salvarRascunho(brand: string, a: Arte): void {
  try {
    localStorage.setItem(`${RASCUNHO}:${brand}`, JSON.stringify(a));
  } catch {
    // Sem rascunho a tela funciona igual. Não vale interromper por isto.
  }
}

// ------------------------------------------------------------------ campos

function campo(
  rotulo: string,
  controle: HTMLElement,
  dica?: string
): HTMLElement {
  return el(
    "div",
    { class: "campo" },
    el("label", {}, rotulo),
    controle,
    dica ? el("p", { class: "sub" }, dica) : null
  );
}

function entrada(
  valor: string,
  aoMudar: (v: string) => void
): HTMLInputElement {
  const i = el("input", { type: "text", value: valor }) as HTMLInputElement;
  i.value = valor;
  i.addEventListener("input", () => aoMudar(i.value));
  return i;
}

function area(
  valor: string,
  linhas: number,
  aoMudar: (v: string) => void
): HTMLTextAreaElement {
  const t = el("textarea", { rows: String(linhas) }) as HTMLTextAreaElement;
  t.value = valor;
  t.addEventListener("input", () => aoMudar(t.value));
  return t;
}

// --------------------------------------------------- os quatro modelos

/** O editor de uma arte por vez, do sistema visual do AntiFila. */
async function editorQuatroModelos(
  raiz: HTMLElement,
  marca: Marca
): Promise<void> {
  let arte = lerRascunho(marca.slug);

  /** Fotos já carregadas, por slot. */
  const fotos: Fotos = {};
  /** Cache por id, para não rebuscar a mesma foto a cada redesenho. */
  const cache = new Map<number, HTMLImageElement>();
  let biblioteca: MidiaGuardada[] = [];
  let logos: LogoGuardado[] = [];

  // ---------------------------------------------------------- canvas
  const tela = el("canvas", { class: "arte-preview" }) as HTMLCanvasElement;
  tela.width = LARGURA;
  tela.height = ALTURA;
  const ctx = tela.getContext("2d");
  if (!ctx) {
    raiz.appendChild(
      el("p", { class: "aviso erro" }, "Este navegador não tem canvas 2D.")
    );
    return;
  }

  const legenda = el("p", { class: "sub" });
  const alerta = el("p", { class: "" });
  const painelCampos = el("div", {});
  const painelFotos = el("div", {});
  const painelLogos = el("div", {});

  let salvar: ReturnType<typeof setTimeout> | undefined;

  const redesenhar = (): void => {
    const p = plano(arte);
    desenhar(ctx, p, fotos, { guias: true });

    const passou = estouro(arte);
    legenda.textContent =
      `As linhas tracejadas marcam o corte 1:1 da miniatura no perfil. ` +
      `O texto termina em ${Math.round(p.fim)} de ${SEGURO_BASE}.`;

    if (passou === null) {
      alerta.className = "";
      alerta.textContent = "";
    } else {
      alerta.className = "aviso";
      alerta.textContent =
        `O texto passa da zona segura em ${passou} px. ` +
        `Suba o bloco, corte uma linha — ou gere assim mesmo, sabendo que ` +
        `essa parte some na grade do perfil.`;
    }

    clearTimeout(salvar);
    salvar = setTimeout(() => salvarRascunho(marca.slug, arte), 400);
  };

  /** Mexeu em algo que muda quais campos aparecem. */
  const remontar = (): void => {
    montarCampos();
    montarFotos();
    montarLogos();
    redesenhar();
  };

  // ------------------------------------------------------------ marca
  //
  // O logo é a única coisa do sistema visual que MUDA por projeto. Todo o
  // resto — cor, tipografia, margem, hierarquia — é fixo, e é isso que o
  // editor compra. A marca é o que o torna utilizável fora do AntiFila.

  const usarLogo = (origem: "nenhum" | "vetor" | number): void => {
    arte.logo = origem;

    if (typeof origem !== "number") {
      fotos.logo = null;
      remontar();
      return;
    }

    const pronto = cache.get(-origem);
    if (pronto) {
      fotos.logo = pronto;
      remontar();
      return;
    }

    const img = new Image();
    // Mesma origem, mesmo motivo das fotos: outra origem contamina o
    // canvas e o toBlob() passa a lançar.
    img.src = `/logos/${origem}/arquivo`;
    img.addEventListener("load", () => {
      // Chave negativa: o cache é compartilhado com as fotos, e id de logo
      // e id de mídia são sequências independentes que colidiriam.
      cache.set(-origem, img);
      fotos.logo = img;
      redesenhar();
    });
    img.addEventListener("error", () => {
      avisar(
        raiz,
        new Error(`Não foi possível carregar o logo ${origem}.`),
        "Logo"
      );
    });
    remontar();
  };

  function montarLogos(): void {
    limpar(painelLogos);
    const grade = el("div", { class: "escolher-midia escolher-foto" });

    const opcao = (
      marcado: boolean,
      rotulo: string,
      conteudo: HTMLElement,
      aoClicar: () => void
    ): HTMLElement => {
      const b = el("button", {
        class: marcado ? "opcao-midia marcada" : "opcao-midia",
        type: "button",
        title: rotulo
      });
      b.appendChild(conteudo);
      b.addEventListener("click", aoClicar);
      return b;
    };

    grade.appendChild(
      opcao(
        arte.logo === "nenhum",
        "Sem logo",
        el("span", { class: "sem-foto" }, "sem logo"),
        () => usarLogo("nenhum")
      )
    );
    grade.appendChild(
      opcao(
        arte.logo === "vetor",
        "AntiFila (vetor)",
        el("span", { class: "sem-foto" }, "AntiFila vetor"),
        () => usarLogo("vetor")
      )
    );

    for (const l of logos) {
      const img = el("img", { alt: l.nome }) as HTMLImageElement;
      img.src = `/logos/${l.logoId}/arquivo`;
      // Fundo xadrez claro: logo é branco na maioria das marcas, e sobre o
      // papel branco da UI ele sumiria — a miniatura ficaria vazia e
      // pareceria arquivo quebrado.
      const caixa = el("span", { class: "mini-logo" }, img);
      grade.appendChild(
        opcao(arte.logo === l.logoId, l.nome, caixa, () => usarLogo(l.logoId))
      );
    }

    painelLogos.appendChild(campo("Marca", grade));

    if (arte.logo === "nenhum") {
      painelLogos.appendChild(
        el(
          "p",
          { class: "sub" },
          "Esta arte vai sair SEM logo. É o padrão de propósito — o editor " +
            "serve todos os projetos, e herdar a marca do anterior é o erro " +
            "que só aparece depois de publicado."
        )
      );
    }
  }

  // ----------------------------------------------------------- fotos
  const usarFoto = (slot: "A" | "B", id: number | null): void => {
    if (slot === "A") arte.fotoA = id;
    else arte.fotoB = id;

    if (id === null) {
      fotos[slot] = null;
      remontar();
      return;
    }

    const pronta = cache.get(id);
    if (pronta) {
      fotos[slot] = pronta;
      remontar();
      return;
    }

    const img = new Image();
    // Mesma origem: sem isto o canvas seria contaminado e o toBlob falharia.
    img.src = `/media/${id}/arquivo`;
    img.addEventListener("load", () => {
      cache.set(id, img);
      fotos[slot] = img;
      redesenhar();
    });
    img.addEventListener("error", () => {
      avisar(
        raiz,
        new Error(`Não foi possível carregar a mídia ${id}.`),
        "Foto"
      );
    });
    remontar();
  };

  function montarFotos(): void {
    limpar(painelFotos);
    const slots = SLOTS[arte.tpl];
    if (slots.length === 0) return;

    for (const { slot, rotulo } of slots) {
      const atual = slot === "A" ? arte.fotoA : arte.fotoB;
      const grade = el("div", { class: "escolher-midia escolher-foto" });

      const nenhuma = el(
        "button",
        {
          class: atual === null ? "opcao-midia marcada" : "opcao-midia",
          type: "button"
        },
        el("span", { class: "sem-foto" }, "sem foto")
      );
      nenhuma.addEventListener("click", () => usarFoto(slot, null));
      grade.appendChild(nenhuma);

      for (const m of biblioteca) {
        const b = el("button", {
          class: atual === m.mediaId ? "opcao-midia marcada" : "opcao-midia",
          type: "button"
        });
        const img = el("img", {
          alt: `mídia ${m.mediaId}`
        }) as HTMLImageElement;
        img.src = `/media/${m.mediaId}/arquivo`;
        b.appendChild(img);
        b.addEventListener("click", () => usarFoto(slot, m.mediaId));
        grade.appendChild(b);
      }

      painelFotos.appendChild(campo(rotulo, grade));
    }
  }

  // ---------------------------------------------------------- campos
  function montarCampos(): void {
    limpar(painelCampos);
    const t = arte.tpl;

    if (t !== "passo") {
      painelCampos.appendChild(
        campo(
          "Kicker",
          entrada(arte.kicker, (v) => {
            arte.kicker = v;
            redesenhar();
          }),
          "A linha de cima, em monoespaçada. Por convenção vai em caixa alta."
        )
      );
    }

    if (t === "passo") {
      const numero = entrada(arte.numero, (v) => {
        arte.numero = v;
        redesenhar();
      });
      const total = entrada(String(arte.total), (v) => {
        arte.total = v;
        redesenhar();
      });
      painelCampos.appendChild(
        campo(
          "Número do passo",
          numero,
          "Por convenção com zero à esquerda: 01, 02…"
        )
      );
      painelCampos.appendChild(
        campo(
          "Total de passos",
          total,
          "De 1 a 9 — é o que define a barra de progresso."
        )
      );
    }

    painelCampos.appendChild(
      campo(
        "Título",
        area(arte.titulo, 3, (v) => {
          arte.titulo = v;
          redesenhar();
        }),
        "Enter quebra a linha. Não há quebra automática: quem decide onde quebra é você."
      )
    );

    if (t === "manif") {
      painelCampos.appendChild(
        campo(
          "Palavra em destaque",
          entrada(arte.destaque, (v) => {
            arte.destaque = v;
            redesenhar();
          }),
          "Sai em lilás dentro do título, na primeira vez que aparecer."
        )
      );
      painelCampos.appendChild(
        campo(
          "Rodapé",
          area(arte.rodape, 2, (v) => {
            arte.rodape = v;
            redesenhar();
          }),
          "Fica fixo embaixo do divisor — não sobe com o controle de altura."
        )
      );
    }

    if (t === "foto" || t === "passo") {
      painelCampos.appendChild(
        campo(
          "Apoio",
          area(arte.apoio, 3, (v) => {
            arte.apoio = v;
            redesenhar();
          })
        )
      );
    }

    if (t === "comp") {
      for (const lado of ["A", "B"] as const) {
        const nome = lado === "A" ? "esquerda" : "direita";
        painelCampos.appendChild(
          campo(
            `Rótulo da ${nome}`,
            entrada(lado === "A" ? arte.rotuloA : arte.rotuloB, (v) => {
              if (lado === "A") arte.rotuloA = v;
              else arte.rotuloB = v;
              redesenhar();
            })
          )
        );
        painelCampos.appendChild(
          campo(
            `Texto da ${nome}`,
            area(lado === "A" ? arte.textoA : arte.textoB, 4, (v) => {
              if (lado === "A") arte.textoA = v;
              else arte.textoB = v;
              redesenhar();
            }),
            "Uma linha por item."
          )
        );
      }
    }

    // ------------------------------------------------------- slider
    const leitura = el("span", { class: "mono" });
    const escrever = (): void => {
      leitura.textContent = `${arte.dy > 0 ? "+" : ""}${arte.dy} px`;
    };
    escrever();

    const faixa = el("input", {
      type: "range",
      min: "-320",
      max: "320",
      step: "10"
    }) as HTMLInputElement;
    faixa.value = String(arte.dy);
    faixa.addEventListener("input", () => {
      arte.dy = clampDy(Number(faixa.value));
      escrever();
      redesenhar();
    });

    const zerar = el(
      "button",
      { class: "secundario pequeno", type: "button" },
      "zerar"
    );
    zerar.addEventListener("click", () => {
      arte.dy = 0;
      faixa.value = "0";
      escrever();
      redesenhar();
    });

    painelCampos.appendChild(
      campo(
        "Altura do bloco de texto",
        el("div", { class: "acoes-linha" }, faixa, leitura, zerar),
        arte.tpl === "foto"
          ? "Neste modelo o controle move também a borda da foto."
          : "Move só o bloco de texto. A assinatura da peça fica onde está."
      )
    );
  }

  // ---------------------------------------------------------- gerar
  const botaoGerar = el(
    "button",
    {},
    "Gerar arte e mandar para a biblioteca"
  ) as HTMLButtonElement;
  const saida = el("p", { class: "sub" });

  botaoGerar.addEventListener("click", () => {
    const passou = estouro(arte);
    if (
      passou !== null &&
      !confirm(
        `O texto passa da zona segura em ${passou} px.\n\n` +
          `A arte é gerada do mesmo jeito, mas essa parte não aparece na ` +
          `miniatura 1:1 da grade do perfil — só abrindo o post.\n\n` +
          `Gerar assim mesmo?`
      )
    )
      return;

    botaoGerar.disabled = true;
    saida.className = "sub";
    saida.textContent = "Desenhando…";

    void (async () => {
      try {
        // Canvas próprio, SEM as guias. Elas nunca podem entrar no
        // arquivo: seriam publicadas junto com a arte.
        const fora = document.createElement("canvas");
        fora.width = LARGURA;
        fora.height = ALTURA;
        const c = fora.getContext("2d");
        if (!c)
          throw new Error("Não foi possível criar o canvas de exportação.");
        desenhar(c, plano(arte), fotos, { guias: false });

        const blob = await new Promise<Blob>((ok, erro) => {
          fora.toBlob(
            (b) =>
              b
                ? ok(b)
                : erro(new Error("O navegador não devolveu os bytes da arte.")),
            "image/png"
          );
        });

        saida.textContent = "Enviando…";
        const r = await Api.enviarMidia(marca.slug, blob);

        saida.className = "saida ok";
        saida.textContent = r.deduped
          ? `Esta arte já estava na biblioteca, como mídia #${r.mediaId}.`
          : `Pronta: mídia #${r.mediaId}, ${r.width}×${r.height}. ` +
            `Já dá para usar em "Nova peça".`;

        // A arte nova vira foto candidata das próximas — e o seletor
        // precisa enxergá-la sem recarregar a página.
        biblioteca = (await Api.midias(marca.slug, "image")).media;
        montarFotos();
      } catch (err) {
        saida.className = "erro-linha";
        saida.textContent = err instanceof Error ? err.message : String(err);
      } finally {
        botaoGerar.disabled = false;
      }
    })();
  });

  // ----------------------------------------------------- upload de logo
  const entradaLogo = el("input", {
    type: "file",
    accept: "image/png"
  }) as HTMLInputElement;
  const nomeLogo = el("input", {
    type: "text",
    placeholder: "nome da marca"
  }) as HTMLInputElement;
  const botaoLogo = el(
    "button",
    { class: "secundario" },
    "Enviar logo"
  ) as HTMLButtonElement;
  const saidaLogo = el("span", { class: "sub" });

  botaoLogo.addEventListener("click", () => {
    const arquivo = entradaLogo.files?.[0];
    if (!arquivo) {
      saidaLogo.className = "sub";
      saidaLogo.textContent = "Escolha um PNG.";
      return;
    }
    const nome = nomeLogo.value.trim() || arquivo.name.replace(/\.png$/i, "");
    botaoLogo.disabled = true;
    saidaLogo.className = "sub";
    saidaLogo.textContent = "Enviando…";

    void (async () => {
      try {
        const r = await Api.enviarLogo(marca.slug, arquivo, nome);
        logos = (await Api.logos(marca.slug)).logos;
        saidaLogo.className = r.temAlfa ? "saida ok" : "aviso";
        saidaLogo.textContent = r.temAlfa
          ? `${r.nome}: ${r.width}×${r.height}`
          : `${r.nome} subiu, mas o PNG é OPACO — vai aparecer com fundo. ` +
            `Reexporte com transparência.`;
        entradaLogo.value = "";
        nomeLogo.value = "";
        usarLogo(r.logoId);
      } catch (err) {
        saidaLogo.className = "erro-linha";
        saidaLogo.textContent =
          err instanceof Error ? err.message : String(err);
      } finally {
        botaoLogo.disabled = false;
      }
    })();
  });

  // ---------------------------------------------------------- upload
  const entradaArquivo = el("input", {
    type: "file",
    accept: "image/*",
    multiple: true
  }) as HTMLInputElement;
  const botaoSubir = el(
    "button",
    { class: "secundario" },
    "Enviar fotos"
  ) as HTMLButtonElement;
  const saidaUpload = el("span", { class: "sub" });

  botaoSubir.addEventListener("click", () => {
    const arquivos = [...(entradaArquivo.files ?? [])];
    if (arquivos.length === 0) {
      saidaUpload.textContent = "Escolha ao menos um arquivo.";
      return;
    }
    botaoSubir.disabled = true;

    void (async () => {
      let enviados = 0;
      let falhas = 0;
      // Em série, como na tela de Mídia: upload é bufferizado em memória
      // dos dois lados, e vários juntos derrubam a API pelo teto do PM2.
      for (const a of arquivos) {
        saidaUpload.textContent = `Enviando ${a.name}…`;
        try {
          await Api.enviarMidia(marca.slug, a);
          enviados++;
        } catch {
          falhas++;
        }
      }
      entradaArquivo.value = "";
      botaoSubir.disabled = false;
      saidaUpload.textContent = `${enviados} enviada(s)${falhas ? `, ${falhas} falhou(aram)` : ""}.`;
      biblioteca = (await Api.midias(marca.slug, "image")).media;
      montarFotos();
    })();
  });

  // ------------------------------------------------------- montagem
  const seletorModelo = el("div", { class: "modelos" });
  const pintarModelos = (): void => {
    limpar(seletorModelo);
    for (const m of MODELOS) {
      const b = el(
        "button",
        {
          class: m.id === arte.tpl ? "modelo marcado" : "modelo",
          type: "button"
        },
        el("strong", {}, m.nome),
        el("span", { class: "sub" }, m.resumo)
      );
      b.addEventListener("click", () => {
        // Trocar de modelo PRESERVA tudo que já foi escrito — o modelo só
        // muda o que é exibido e desenhado. É o que permite experimentar
        // layout sem perder texto.
        arte.tpl = m.id;
        pintarModelos();
        remontar();
      });
      seletorModelo.appendChild(b);
    }
  };

  const coluna = el(
    "div",
    { class: "arte-editor" },
    el(
      "div",
      {},
      el("div", { class: "caixa" }, el("h3", {}, "Modelo"), seletorModelo),
      el("div", { class: "caixa" }, el("h3", {}, "Conteúdo"), painelCampos),
      el(
        "div",
        { class: "caixa" },
        el("h3", {}, "Marca"),
        el(
          "p",
          { class: "sub" },
          "PNG com fundo transparente. JPEG é recusado: sem canal alfa, " +
            "ele traria um retângulo de fundo junto da marca."
        ),
        el(
          "div",
          { class: "acoes-linha" },
          entradaLogo,
          nomeLogo,
          botaoLogo,
          saidaLogo
        ),
        painelLogos
      ),
      el(
        "div",
        { class: "caixa" },
        el("h3", {}, "Fotos"),
        el(
          "p",
          { class: "sub" },
          "Da biblioteca de mídia — a mesma que a tela de Mídia alimenta."
        ),
        el(
          "div",
          { class: "acoes-linha" },
          entradaArquivo,
          botaoSubir,
          saidaUpload
        ),
        painelFotos
      )
    ),
    el(
      "div",
      { class: "arte-lado" },
      el(
        "div",
        { class: "caixa" },
        tela,
        legenda,
        alerta,
        el("div", { class: "acoes" }, botaoGerar),
        saida
      )
    )
  );

  raiz.appendChild(coluna);

  // A ORDEM IMPORTA: fontes primeiro.
  //
  // `measureText` com a fonte de fallback devolve métrica errada, e a
  // palavra em destaque do manifesto sai fora de lugar — sem erro nenhum.
  // Por isso desenha-se uma vez para a tela não nascer em branco, e de
  // novo depois que tudo chegou.
  pintarModelos();
  remontar();

  try {
    await carregarFontes();
  } catch {
    // Fonte que não carrega dá arte torta, não tela quebrada.
  }

  try {
    logos = (await Api.logos(marca.slug)).logos;
    if (typeof arte.logo === "number") usarLogo(arte.logo);
  } catch (err) {
    avisar(raiz, err, "Não deu para listar os logos");
  }

  try {
    biblioteca = (await Api.midias(marca.slug, "image")).media;
    // Repõe as fotos do rascunho depois de saber o que existe.
    if (arte.fotoA !== null) usarFoto("A", arte.fotoA);
    if (arte.fotoB !== null) usarFoto("B", arte.fotoB);
  } catch (err) {
    avisar(raiz, err, "Não deu para listar a biblioteca");
  }

  montarFotos();
  montarLogos();
  redesenhar();
}
